/**
 * Altitude-coloured flight ramp. Input is metres; output is a CSS hex.
 * Inspired by worldmonitor's altitude gradient (AGPL-3.0); ramp designed here.
 */

/**
 * Canonical SupplyLens globe palette. Mirrors the CSS custom properties
 * (--accent, --fleet, …) so the WebGL layer and the DOM chrome stay in lockstep.
 * Shaders consume the `*_RGB` normalised triples; DOM/material code uses the hex.
 */
export const GLOBE_PALETTE = {
  accent: "#22d3ee", // --accent : cyan rim / generic arcs
  fleet: "#facc15", // --fleet  : owned assets / fleet arcs
  incident: "#ef4444", // disruption / critical incidents
  flight: "#a5f3fc", // live flights
  ship: "#67e8f9", // vessels
  bg: "#0a0a0a", // canvas background
  // Secondary tones used by the columns/atmosphere for depth.
  atmosphereDeep: "#0b3a4d", // deep blue the fresnel fades into near the body
  incidentHigh: "#f97316", // high (non-critical) incident column
  incidentMed: "#f59e0b", // medium incident column
} as const

export type GlobePaletteKey = keyof typeof GLOBE_PALETTE

/** Normalised [r,g,b] in 0..1 for a hex string — handy for shader uniforms. */
export function hexToRgb01(hex: string): [number, number, number] {
  const h = hex.replace("#", "")
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h
  const n = Number.parseInt(full, 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

/** Incident-severity → column colour (hex), used by the risk-column layer. */
export function severityColumnColor(severity: string | undefined): string {
  switch (severity) {
    case "critical":
      return GLOBE_PALETTE.incident
    case "high":
    case "warn":
      return GLOBE_PALETTE.incidentHigh
    case "medium":
      return GLOBE_PALETTE.incidentMed
    default:
      return GLOBE_PALETTE.ship // info/low → calm cyan, low visual weight
  }
}

const STOPS: ReadonlyArray<{ alt_m: number; r: number; g: number; b: number }> = [
  { alt_m: 0, r: 0, g: 217, b: 255 },
  { alt_m: 1500, r: 50, g: 250, b: 160 },
  { alt_m: 3000, r: 200, g: 230, b: 60 },
  { alt_m: 6000, r: 255, g: 180, b: 40 },
  { alt_m: 9000, r: 255, g: 110, b: 35 },
  { alt_m: 12000, r: 235, g: 50, b: 55 },
  { alt_m: 14000, r: 210, g: 40, b: 70 },
]

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t)
}

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`
}

export function altitudeColor(altM: number | undefined | null): string {
  const alt = Number.isFinite(altM) ? Number(altM) : 0
  if (alt <= STOPS[0]!.alt_m) return toHex(STOPS[0]!.r, STOPS[0]!.g, STOPS[0]!.b)
  const last = STOPS[STOPS.length - 1]!
  if (alt >= last.alt_m) return toHex(last.r, last.g, last.b)
  for (let i = 1; i < STOPS.length; i++) {
    const hi = STOPS[i]!
    const lo = STOPS[i - 1]!
    if (alt <= hi.alt_m) {
      const t = (alt - lo.alt_m) / (hi.alt_m - lo.alt_m)
      return toHex(lerp(lo.r, hi.r, t), lerp(lo.g, hi.g, t), lerp(lo.b, hi.b, t))
    }
  }
  return toHex(last.r, last.g, last.b)
}

/** Standard great-circle interpolation for drawing arcs on a sphere. */
export function greatCirclePoints(
  a: [number, number],
  b: [number, number],
  n = 32,
): [number, number][] {
  const toRad = (d: number) => (d * Math.PI) / 180
  const toDeg = (r: number) => (r * 180) / Math.PI
  const [lon1, lat1] = [toRad(a[0]), toRad(a[1])]
  const [lon2, lat2] = [toRad(b[0]), toRad(b[1])]
  const d =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2,
      ),
    )
  if (d === 0) return [a, b]
  const points: [number, number][] = []
  for (let i = 0; i <= n; i++) {
    const f = i / n
    const A = Math.sin((1 - f) * d) / Math.sin(d)
    const B = Math.sin(f * d) / Math.sin(d)
    const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2)
    const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2)
    const z = A * Math.sin(lat1) + B * Math.sin(lat2)
    points.push([
      toDeg(Math.atan2(y, x)),
      toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))),
    ])
  }
  return points
}
